/**
 * CookieConsent — bannière de consentement cookies minimaliste.
 *
 * Affichage :
 * - Apparaît en bas de l'écran à la première visite (pas de choix en localStorage)
 * - Disparaît après "Accepter" ou "Refuser"
 * - Lien "En savoir plus" → page CGV (§ cookies)
 *
 * Conformité :
 * - Active Google Consent Mode v2 (analytics_storage + ad_storage)
 * - Choix mémorisé sous la clé `signapharma_cookie_consent` (valeur:
 *   "accepted" ou "denied")
 *
 * Design : bandeau fixe, dark + accent vert néon, ne masque pas la page.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { setConsent } from "../hooks/useAnalytics";

const STORAGE_KEY = "signapharma_cookie_consent";

const COPY = {
  fr: {
    title: "Cookies & confidentialité",
    body: "Signapharma utilise des cookies analytiques (Google Analytics) pour mesurer la fréquentation du site et améliorer votre expérience. Aucune donnée personnelle n'est partagée.",
    accept: "Accepter",
    reject: "Refuser",
    learn: "En savoir plus",
    close: "Fermer",
  },
  de: {
    title: "Cookies & Datenschutz",
    body: "Signapharma verwendet Analyse-Cookies (Google Analytics), um die Website zu verbessern. Keine personenbezogenen Daten werden geteilt.",
    accept: "Akzeptieren",
    reject: "Ablehnen",
    learn: "Mehr erfahren",
    close: "Schliessen",
  },
  en: {
    title: "Cookies & privacy",
    body: "Signapharma uses analytics cookies (Google Analytics) to measure traffic and improve your experience. No personal data is shared.",
    accept: "Accept",
    reject: "Reject",
    learn: "Learn more",
    close: "Close",
  },
  it: {
    title: "Cookie & privacy",
    body: "Signapharma utilizza cookie analitici (Google Analytics) per migliorare il sito. Nessun dato personale viene condiviso.",
    accept: "Accetta",
    reject: "Rifiuta",
    learn: "Ulteriori informazioni",
    close: "Chiudi",
  },
};

export default function CookieConsent() {
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);

  // Lecture du choix initial — bannière affichée uniquement si pas de choix
  useEffect(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      setOpen(v !== "accepted" && v !== "denied");
    } catch {
      setOpen(true);
    }
  }, []);

  if (!open) return null;
  const t = COPY[lang] || COPY.fr;

  const decide = (accepted) => {
    setConsent(accepted);
    setOpen(false);
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] bg-[#0a0a0a] border-t border-[#39FF14]/40 shadow-[0_-8px_32px_rgba(57,255,20,0.08)]"
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-label={t.title}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4 flex flex-col md:flex-row items-start md:items-center gap-4">
        <Cookie size={22} className="text-[#39FF14] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#39FF14] mb-1">
            {t.title}
          </p>
          <p className="text-sm text-zinc-300 leading-relaxed">
            {t.body}{" "}
            <Link
              to="/cgv"
              className="text-[#39FF14] underline hover:no-underline"
              data-testid="cookie-consent-learn-more"
            >
              {t.learn} →
            </Link>
          </p>
        </div>
        <div className="flex gap-2 shrink-0 w-full md:w-auto">
          <button
            type="button"
            onClick={() => decide(false)}
            className="flex-1 md:flex-none px-4 py-2 border border-white/15 hover:border-white/30 text-zinc-400 hover:text-white font-mono text-[11px] uppercase tracking-widest transition-colors"
            data-testid="cookie-consent-reject"
          >
            {t.reject}
          </button>
          <button
            type="button"
            onClick={() => decide(true)}
            className="flex-1 md:flex-none btn-neon"
            data-testid="cookie-consent-accept"
          >
            {t.accept}
          </button>
        </div>
        <button
          type="button"
          onClick={() => decide(false)}
          aria-label={t.close}
          className="hidden md:block absolute top-2 right-2 p-1 text-zinc-600 hover:text-white"
          data-testid="cookie-consent-close"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
